import {
  useSubmissionTimeline,
  TimelineTimestamps,
} from '@/hooks/submissions/useSubmissionTimeline';
import Timeline, { TimelineStep } from '@/components/Timeline';

// Order matters: index in this array is the step's progress level, consumed
// by both getProgressLevel (via indexOf) and generateTimelineSteps (via
// TRACKER_STEPS below) so inserting/removing a status only ever requires
// editing this one list.
const STATUS_STEPS = [
  'draft',
  'submitted',
  'pre_review',
  'irb_review',
  'post_review',
  'complete',
];

// The steps actually shown on the tracker (draft has no step of its own —
// the tracker starts once a submission is submitted), in display order.
// timestampKey/statusValue drive both the "completed" check and the date
// shown; label/id are display-only.
const TRACKER_STEPS: Array<{
  id: string;
  statusValue: string;
  label: string;
  timestampKey: keyof TimelineTimestamps;
}> = [
  {
    id: 'submitted',
    statusValue: 'submitted',
    label: 'Submitted',
    timestampKey: 'submitted_at',
  },
  {
    id: 'pre-review',
    statusValue: 'pre_review',
    label: 'Pre-review',
    timestampKey: 'pre_review_at',
  },
  {
    id: 'review',
    statusValue: 'irb_review',
    label: 'Review',
    timestampKey: 'review_at',
  },
  {
    id: 'post-review',
    statusValue: 'post_review',
    label: 'Post-review',
    timestampKey: 'post_review_at',
  },
  {
    id: 'complete',
    statusValue: 'complete',
    label: 'Complete',
    timestampKey: 'complete_at',
  },
];

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getProgressLevel = (
  status: string,
  timestamps: TimelineTimestamps
): number => {
  const index = STATUS_STEPS.indexOf(status);
  if (index >= 0) return index;

  if (status === 'expired') {
    if (timestamps.post_review_at) return STATUS_STEPS.indexOf('post_review');
    if (timestamps.review_at) return STATUS_STEPS.indexOf('irb_review');
    if (timestamps.pre_review_at) return STATUS_STEPS.indexOf('pre_review');
    if (timestamps.submitted_at) return STATUS_STEPS.indexOf('submitted');
    return 0;
  }

  return 0;
};

const generateTimelineSteps = (
  status: string,
  timestamps: TimelineTimestamps,
  subStatusLabel?: string | null
): TimelineStep[] => {
  const level = getProgressLevel(status, timestamps);

  return TRACKER_STEPS.map(({ id, statusValue, label, timestampKey }) => {
    const stepLevel = STATUS_STEPS.indexOf(statusValue);
    const date = timestamps[timestampKey];

    // The `complete` step is terminal — once reached it's "completed", not
    // "in progress", unlike every other step which is "current" while the
    // submission actually sits at that status.
    let stepStatus: TimelineStep['status'];
    if (
      level > stepLevel ||
      (level === stepLevel && statusValue === 'complete')
    ) {
      stepStatus = 'completed';
    } else if (level === stepLevel) {
      stepStatus = 'current';
    } else {
      stepStatus = 'pending';
    }

    return {
      id,
      label,
      status: stepStatus,
      date: date ? formatDate(date) : undefined,
      subStatusLabel:
        stepStatus === 'current' && subStatusLabel ? subStatusLabel : undefined,
    };
  });
};

const Status = ({
  status,
  submissionId,
  title,
  subStatusLabel,
}: {
  status: string;
  submissionId: string;
  title?: string;
  subStatusLabel?: string | null;
}) => {
  const { timelineData, isLoading, error } =
    useSubmissionTimeline(submissionId);

  const steps = generateTimelineSteps(
    status,
    timelineData || {},
    subStatusLabel
  );

  return (
    <Timeline
      steps={steps}
      loading={isLoading}
      error={error ?? undefined}
      title={title}
    />
  );
};

export default Status;
